// MUI から Box、Grid、Pagination コンポーネントをインポート
import { Box, Grid, Pagination } from '@mui/material';
import type {
  AgGridEvent,
  ColDef,
  ColGroupDef,
  GetContextMenuItems,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowDoubleClickedEvent,
  RowNode,
  SelectionChangedEvent,
} from 'ag-grid-community';
// React から useRef, useState フックをインポート
import React, { useEffect, useRef, useState } from 'react';
// react-i18next から useTranslation フックをインポート
import { useTranslation } from 'react-i18next';
// react-router-dom から useNavigate フックをインポート
import { useNavigate } from 'react-router-dom';
// カレンダー関連データ取得ユーティリティをインポート
import { useCalendarApiEntity } from 'src/api/useCalendarApi';
import { StyledAgGrid } from 'src/components/app-components/StyledAgGrid';
import StyledDialog from 'src/components/app-components/StyledDialog';
import { StyledLoading } from 'src/components/app-components/StyledLoading';
import ThreeDotMenuRenderer from 'src/components/app-components/ThreeDotMenuRenderer';
import { LoadableComponent } from 'src/components/app-components/bonsai/LoadableComponent';
// LIST_STATE_KEY_PREFIX）をインポート
import { CREATE_USER_SYSTEM, LIST_STATE_KEY_PREFIX } from 'src/constants';
import { API_PATH } from 'src/features/general/routes/generalPath';
// ロケールフックをインポート
import useLocales from 'src/hooks/useLocales';
import useSettings from 'src/hooks/useSettings';
// Redux の RootState、useAppSelector、useAppDispatch をインポート
import { Checkbox } from '@mui/material';
import { RootState, useAppDispatch, useAppSelector } from 'src/redux/store';
import { showUserName } from 'src/utils/gridValueUtils';
import { switchLabel } from 'src/utils/labelUtils';
import { useCalendarListGrid } from '../../hooks/useCalendarListGrid';
import {
  setClearSearchFlag,
  setDisabled,
  setIsDestroyed,
  setIsLoading,
  setIsOpen,
  setMaxRow,
  setPage,
  setTempSelectedCalendarList,
  setUseSortModel
} from '../../store/enqueteCalendarSlice';
// カレンダー一覧を表示する。
export const CalendarListGrid: React.FC = (CalendarListGridProps): React.ReactElement => {
  const listData = useAppSelector((state: RootState) => state.enqueteCalendar.enqueteCalendarlimitedlistData);
  const tempSelectedCalendarList = useAppSelector((state) => state.enqueteCalendar.tempSelectedCalendarList);
  // 削除ダイアログ
  const isOpen = useAppSelector((state) => state.enqueteCalendar.isOpen);

  const hook = useCalendarListGrid();
  // Redux dispatch 関数を取得
  const dispatch = useAppDispatch();
  // 翻訳関数を取得
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // 現在の言語設定を取得
  const { currentLang } = useLocales();
  const startRowPosition = useAppSelector((state) => state.enqueteCalendar.startRowPosition);
  const startPagePosition = useAppSelector((state) => state.enqueteCalendar.startPagePosition);
  // アンケート選択値
  const surveyIds = useAppSelector((state) => state.enqueteCalendar.surveyIds);
  // 年
  const year = useAppSelector((state) => state.enqueteCalendar.year);
  // 月
  const month = useAppSelector((state) => state.enqueteCalendar.month);
  // 日
  const day = useAppSelector((state) => state.enqueteCalendar.day);
  const storageKey = useAppSelector((state) => state.enqueteCalendar.storageKey);
  const gridApi = useRef<GridApi | null>(null);

  // ローディング
  const isLoading = useAppSelector((state) => state.enqueteCalendar.isLoading);
  const [isStyledLoading, setIsStyledLoading] = useState<boolean>(false);

  // グリッドソートデータ
  const useSortModel = useAppSelector((state) => state.enqueteCalendar.useSortModel);
  // 現在のページ
  const page = useAppSelector((state) => state.enqueteCalendar.page);
  // 1ページ表示数
  const blockSize = useAppSelector((state) => state.enqueteCalendar.blockSize);
  // 最大行数
  const maxRow = useAppSelector((state) => state.enqueteCalendar.maxRow);
  // グリッド表示削除フラグ
  const isDestroyed = useAppSelector((state) => state.enqueteCalendar.isDestroyed);

  // テーマ設定
  const { themeMode } = useSettings();
  const isLight = themeMode === 'light';

  //メッセージ定義
  const deleteConfirmDialogContent = t('enqueteCalendarList.message.deleteConfirmDialogContent');

  //タイトル定義
  const deleteConfirmDialogTitle = t('enqueteCalendarList.message.deleteConfirmDialogTitle');

  useEffect(() => {
    return () => {
      //選択項目をクリアする
      dispatch(setTempSelectedCalendarList([]));
    };
  }, [i18n?.language]);

  //-------------------------------------------------------------
  // グリッドを再描画する
  //-------------------------------------------------------------
  const destroyGrid = () => {
    dispatch(setIsDestroyed(true));
    setTimeout(() => recreateGrid(), 0);
  };
  const recreateGrid = () => {
    dispatch(setIsDestroyed(false));
  };

  // ColDef読み込み
  const coldef: (ColDef | ColGroupDef)[] = [
    // checkbox
    {
      field: 'select',
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      checkboxSelection: true,
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      resizable: false,
      sortable: false,
      filter: false,
      suppressMenu: true,
      pinned: 'left',
    },
    // 適用期間
    {
      field: 'applicablePeriod',
      headerName: t('enqueteCalendarList.gridColDef.applicablePeriod'),
      headerTooltip: t('enqueteCalendarList.gridColDef.applicablePeriod'),
      minWidth: 200,
      filter: 'agDateColumnFilter',
      filterParams: {
        suppressAndOrCondition: true,
        applyMiniFilterWhileTyping: true,
        filterOptions: ['in range'],
      },
      floatingFilter: true,
    },
    // 見出し
    {
      field: currentLang.value === 'ja' ? 'titleJpn' : 'titleEng',
      headerName: t('enqueteCalendarList.gridColDef.heading'),
      headerTooltip: t('enqueteCalendarList.gridColDef.heading'),
      tooltipField: currentLang.value === 'ja' ? 'titleJpn' : 'titleEng',
      minWidth: 200,
      filter: 'text',
      filterParams: {
        suppressAndOrCondition: true,
        applyMiniFilterWhileTyping: true,
        filterOptions: ['contains'],
      },
      floatingFilter: true,
      valueGetter: (params) => {
        return switchLabel(
          params.data.titleJpn ? params.data.titleJpn : params.data.titleEng,
          params.data.titleEng ? params.data.titleEng : params.data.titleJpn,
          currentLang.value === 'ja',
        );
      },
    },
    // Sコード
    {
      field: 'companyCode',
      headerName: t('enqueteCalendarList.gridColDef.companyCode'),
      headerTooltip: t('enqueteCalendarList.gridColDef.companyCode'),
      minWidth: 130,
      filter: 'text',
      filterParams: {
        suppressAndOrCondition: true,
        applyMiniFilterWhileTyping: true,
        filterOptions: ['contains'],
      },
      floatingFilter: true,
    },
    // 格付
    {
      field: 'rank',
      headerName: t('enqueteCalendarList.gridColDef.rank'),
      headerTooltip: t('enqueteCalendarList.gridColDef.rank'),
      minWidth: 100,
      filter: 'text',
      filterParams: {
        suppressAndOrCondition: true,
        applyMiniFilterWhileTyping: true,
        filterOptions: ['contains'],
      },
      floatingFilter: true,
    },
    // 会社
    {
      field: currentLang.value === 'ja' ? 'companyNameJpn' : 'companyNameEng',
      headerName: t('enqueteCalendarList.gridColDef.companyName'),
      headerTooltip: t('enqueteCalendarList.gridColDef.companyName'),
      tooltipField: currentLang.value === 'ja' ? 'companyNameJpn' : 'companyNameEng',
      minWidth: 200,
      filter: 'text',
      filterParams: {
        suppressAndOrCondition: true,
        applyMiniFilterWhileTyping: true,
        filterOptions: ['contains'],
      },
      floatingFilter: true,
      valueGetter: (params) => {
        return switchLabel(params.data.companyNameJpn ?? '', params.data.companyNameEng ?? '', currentLang.value === 'ja');
      },
    },
    // 会社略称
    {
      field: 'companyAbbreviation',
      headerName: t('enqueteCalendarList.gridColDef.companyAbbreviation'),
      headerTooltip: t('enqueteCalendarList.gridColDef.companyAbbreviation'),
      minWidth: 200,
      filter: 'text',
      filterParams: {
        suppressAndOrCondition: true,
        applyMiniFilterWhileTyping: true,
        filterOptions: ['contains'],
      },
      floatingFilter: true,
    },
    // 本部
    {
      field: currentLang.value === 'ja' ? 'divisionNameJpn' : 'divisionNameEng',
      headerName: t('enqueteCalendarList.gridColDef.divisionName'),
      headerTooltip: t('enqueteCalendarList.gridColDef.divisionName'),
      tooltipField: currentLang.value === 'ja' ? 'divisionNameJpn' : 'divisionNameEng',
      minWidth: 200,
      filter: 'text',
      filterParams: {
        suppressAndOrCondition: true,
        applyMiniFilterWhileTyping: true,
        filterOptions: ['contains'],
      },
      floatingFilter: true,
      valueGetter: (params) => {
        return switchLabel(params.data.divisionNameJpn ?? '', params.data.divisionNameEng ?? '', currentLang.value === 'ja');
      },
    },
    // SBU
    {
      field: currentLang.value === 'ja' ? 'sbuNameJpn' : 'sbuNameEng',
      headerName: t('enqueteCalendarList.gridColDef.sbuName'),
      headerTooltip: t('enqueteCalendarList.gridColDef.sbuName'),
      tooltipField: currentLang.value === 'ja' ? 'sbuNameJpn' : 'sbuNameEng',
      minWidth: 200,
      filter: 'text',
      filterParams: {
        suppressAndOrCondition: true,
        applyMiniFilterWhileTyping: true,
        filterOptions: ['contains'],
      },
      floatingFilter: true,
      valueGetter: (params) => {
        return switchLabel(params.data.sbuNameJpn ?? '', params.data.sbuNameEng ?? '', currentLang.value === 'ja');
      },
    },
    // 部
    {
      field: currentLang.value === 'ja' ? 'departmentNameJpn' : 'departmentNameEng',
      headerName: t('enqueteCalendarList.gridColDef.departmentName'),
      headerTooltip: t('enqueteCalendarList.gridColDef.departmentName'),
      tooltipField: currentLang.value === 'ja' ? 'departmentNameJpn' : 'departmentNameEng',
      minWidth: 200,
      filter: 'text',
      filterParams: {
        suppressAndOrCondition: true,
        applyMiniFilterWhileTyping: true,
        filterOptions: ['contains'],
      },
      floatingFilter: true,
      valueGetter: (params) => {
        return switchLabel(params.data.departmentNameJpn ?? '', params.data.departmentNameEng ?? '', currentLang.value === 'ja');
      },
    },
    // 作成者
    {
      field: 'createdUserId',
      headerName: t('enqueteCalendarList.gridColDef.creator'),
      headerTooltip: t('enqueteCalendarList.gridColDef.creator'),
      minWidth: 150,
      filter: 'text',
      filterParams: {
        suppressAndOrCondition: true,
        applyMiniFilterWhileTyping: true,
        filterOptions: ['contains'],
      },
      floatingFilter: true,
      valueGetter: (params) => {
        if (params.data.createdUserId !== CREATE_USER_SYSTEM) {
          return showUserName(params, 'userNameJpn', 'userNameEng', currentLang.value === 'ja');
        } else {
          return params.data.createdUserId;
        }
      },
      tooltipValueGetter: (params) => params.value,
    },
    // リマインド
    {
      field: 'reminder',
      headerName: t('enqueteCalendarList.gridColDef.reminder'),
      headerTooltip: t('enqueteCalendarList.gridColDef.reminder'),
      cellRenderer: (params: any) => {
        return <Checkbox checked={params.data.reminder} readOnly />;
      },
      minWidth: 120,
      filter: true,
      filterParams: {
        values: [true, false],
        suppressSorting: true,
        valueFormatter: (params: any) => {
          return params.value === 'true' ? t('enqueteCalendarList.ReminderYN.yes') : t('enqueteCalendarList.ReminderYN.no');
        },
      },
      floatingFilter: true,
    },
    // 縦三点リーダー
    {
      field: '-',
      headerName: '',
      cellRenderer: ThreeDotMenuRenderer,
      editable: false,
      minWidth: 80,
      width: 50,
      filter: false,
      pinned: 'right',
    },
  ];

  // 最大件数データ取得
  const setCountLimitedRowData = async (
    // 選択したアンケートリスト
    surveyIds: string[],
    // 検索条件：年
    year: string,
    // 検索条件：月
    month: string,
    // 検索条件：日
    day: string,
    // フィルター条件
    useFiletrModel: { [key: string]: any },
    // 現在の言語
    currentLang: string,
  ) => {
    // カレンダーデータ件数を取得
    const rowCount = await hook.getCountLimitedLoadList(surveyIds, year, month, day, useFiletrModel, currentLang);
    dispatch(setMaxRow(rowCount !== undefined ? rowCount : 1));
  };

  // グリッドデータ取得
  const setLimitedRowData = async (
    // 選択したアンケートリスト
    surveyIds: string[],
    // 検索条件：年
    year: string,
    // 検索条件：月
    month: string,
    // 検索条件：日
    day: string,
    // 毎ページ件数
    blockSize: number,
    // 開始行
    startRow: number,
    // ソート条件
    useSortModel: any,
    // フィルター条件
    useFiletrModel: { [key: string]: any },
    // 現在の言語
    currentLang: string,
  ) => {
    //ローディング開始
    dispatch(setIsLoading(true));
    try {
      // カレンダーデータを取得
      await hook.getLimitedLoadList(surveyIds, year, month, day, blockSize, startRow, useSortModel, useFiletrModel, currentLang);
    } finally {
      //ローディング終了
      dispatch(setIsLoading(false));
    }
  };

  // ダブルクリック時の挙動定義
  const onRowDoubleClickedCallback = (event: RowDoubleClickedEvent) => {
    dispatch(setClearSearchFlag(false));
    navigate(`${API_PATH.ENQUETE_CALENDAR}/calendar-edit/${event.data.id}`);
  };

  // 選択行を削除
  const onClickDelete = async () => {
    // スタイルローディング開始
    setIsStyledLoading(true);
    // 選択するカレンダー明細データ
    const calendarIds: string[] = Object.values(tempSelectedCalendarList).map((item) => item.id);
    // 選択するカレンダーを削除
    await hook.deleteRows(calendarIds);
    // 削除確認ダイアログを閉じる
    dispatch(setIsOpen(false));
    // グリッドを再描画する
    destroyGrid();
    // スタイルローディング終了
    setIsStyledLoading(false);
    dispatch(setTempSelectedCalendarList([]));
  };

  //コンテキストメニュー
  const contextMenu: GetContextMenuItems = (params: GetContextMenuItemsParams) => {
    if (params.node === null) return [];
    // カレンダー編集ボタン
    // カレンダー編集押下、カレンダー編集画面へ遷移する
    // パラメータ：カレンダーID
    const menuItems = [
      {
        name: t('enqueteCalendarList.contextMenu.edit'),
        action: () => {
          dispatch(setClearSearchFlag(false));
          navigate(`${API_PATH.ENQUETE_CALENDAR}/calendar-edit/${params.node?.data.id}`);
        },
      },
    ];

    return menuItems;
  };

  // フィルター条件変更処理
  const onFilterChangedCallback = (event: AgGridEvent) => {
    // フィルター条件変更、明細データを再検索
    setFilterSortLimitedRowData();
  };
  // ソート条件とフィルター条件変更時、明細データを再検索
  const setFilterSortLimitedRowData = () => {
    const filterModel = localStorage.getItem(`${LIST_STATE_KEY_PREFIX.FILTER}${storageKey}`);
    const filter = filterModel ?? {};
    // 最大件数データ取得
    setCountLimitedRowData(surveyIds, year, month, day, filter, currentLang.code);
    // ページ設定
    dispatch(setPage(startPagePosition));
    // グリッドデータ取得
    setLimitedRowData(surveyIds, year, month, day, blockSize, startRowPosition, useSortModel, filter, currentLang.code);
  };

  // ソート順変更処理
  const onSortChangedCallback = (event: AgGridEvent) => {
    // ソート条件設定
    dispatch(setUseSortModel(event.columnApi.getColumnState()));
    // ソート条件変更、明細データを再検索
    setFilterSortLimitedRowData();
  };

  // 改ページ処理
  const setChangePgae = (event: React.ChangeEvent<unknown>, value: number) => {
    dispatch(setPage(value));
  };

  // NOTE: グリッドの表示準備完了時の処理
  const onGridReady = (params: GridReadyEvent<useCalendarApiEntity>) => {
    gridApi.current = params.api;
  };

  // NOTE: 初回グリッドがレンダーされた際に行選択を反映
  const firstApplySelectedRow = () => {
    gridApi.current?.forEachNode((node, index) => {
      Object.keys(tempSelectedCalendarList).forEach((rowIndex: string) => {
        if (index === Number(rowIndex)) node.setSelected(true);
      });
    });
  };

  // NOTE: チェックイベント毎に選択中の行データを格納
  const checkedRow = (e: SelectionChangedEvent) => {
    dispatch(setTempSelectedCalendarList(gridApi.current?.getSelectedNodes() as RowNode[]));
    const rowNodes = gridApi.current?.getSelectedNodes() as RowNode[];
    // 作成者がSystem自動作成イベントであるかどうかを判定する
    const exists = rowNodes.some((item) => item.data?.createdUserId === CREATE_USER_SYSTEM);
    if (exists) {
      // System自動作成がある場合、削除できない
      dispatch(setDisabled(true));
    } else {
      // System自動作成がない場合、削除できる
      dispatch(setDisabled(false));
    }
  };

  // StyledAgGridのgridOptions設定
  const gridOptions: GridOptions<useCalendarApiEntity> = {
    rowSelection: 'multiple',
    suppressRowClickSelection: true,
    onSelectionChanged: checkedRow,
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12} md={12}>
          <Box
            className={isLight ? 'ag-theme-alpine' : 'ag-theme-alpine-dark'}
            sx={{
              height: '77vh',
              width: '100%',
            }}>
            <LoadableComponent isLoading={isLoading}>
              {!isDestroyed && (
                <StyledAgGrid
                  // 列定義
                  coldef={coldef}
                  // 表示する行情報
                  rows={listData}
                  // 右クリック時のメニュー表示
                  contextMenu={contextMenu}
                  // 行ダブルクリック後に実行する関数
                  onRowDoubleClickedCallback={onRowDoubleClickedCallback}
                  // ソート条件変更後に実行する関数
                  onSortChangedCallback={onSortChangedCallback}
                  // フィルター条件変更後に実行する関数
                  onFilterChangedCallback={onFilterChangedCallback}
                  // グリッドの設定情報
                  gridOptions={gridOptions}
                  // グリッド初期化完了イベント
                  onGridReady={onGridReady}
                  // 初回データ描画完了イベント
                  onFirstDataRendered={firstApplySelectedRow}
                  // 保存キー
                  storageKey={storageKey}
                />
              )}
              <Grid item xs={12} md={12}>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Pagination count={maxRow <= blockSize ? 1 : Math.ceil(maxRow / blockSize)} page={page} onChange={setChangePgae} />
                </div>
              </Grid>
            </LoadableComponent>
          </Box>
        </Grid>
      </Grid>
      {/* 削除ダイアログ */}
      <StyledDialog
        // ダイアログタイトル
        dialogTitle={deleteConfirmDialogTitle}
        // ダイアログの内容
        dialogContent={deleteConfirmDialogContent}
        // ダイアログオープンフラグ
        isOpen={isOpen}
        // 「いいえ」ボタン押下時に実行されるコールバック関数
        disagreeCallback={() => dispatch(setIsOpen(false))}
        // 「はい」ボタン押下時に実行されるコールバック関数
        agreeCallback={onClickDelete}
      />
      <StyledLoading isOpen={isStyledLoading} />
    </>
  );
};
